import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import type { ReactNode } from "react";

import { ApiError, NetworkError } from "@/shared/api";

/**
 * Дефолты подобраны под rate limit бэкенда: 60 запросов в минуту на чтение,
 * 10 на денежные ручки, и корзина считается по IP. Админку открывают с
 * офисной сети, где IP один на всех операторов, поэтому никакого
 * `refetchInterval` — счётчик общий.
 *
 * `refetchOnWindowFocus` включён, в отличие от мини-аппа: оператор держит
 * админку во вкладке часами, и возвращаться к списку заявок недельной
 * свежести он не должен. `staleTime` при этом короткий по той же причине.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchInterval: false,
        // Повторять имеет смысл только то, что может измениться само: обрыв
        // сети и пятисотку. Повтор 403 или 409 вернёт тот же ответ, зато
        // потратит запрос из общего лимита.
        retry: (failureCount, error) => {
          if (failureCount >= 2) return false;
          if (error instanceof NetworkError) return true;
          return error instanceof ApiError && error.status >= 500;
        },
      },
      // Ретраить мутацию админки нельзя молча: подтверждение заявки не
      // идемпотентно на уровне UI, и второй запрос вернёт 409 вместо успеха.
      mutations: { retry: false },
    },
  });
}

export function QueryProvider({ children }: { children: ReactNode }) {
  const [client] = useState(createQueryClient);
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
